import type {
  ActivitySnapshot,
  PhaseProgressUpdate,
  WaitDescriptor,
} from '@volley/crucible-ipc-types/ipc-channels';
import type {
  AutoBuildState,
  BuildGameInterviewState,
  BuildGameInterviewStatus,
  KnownInterviewDepth,
  KnownInterviewSubPhase,
  PhaseBDecision,
} from '@volley/crucible-ipc-types/build-game-types';
import type { FoundryGameStateSnapshot } from '@volley/crucible/foundry/state-kernel.js';
import { hasVerifiedBuildCompletionEvidence } from '@volley/crucible/foundry/build-completion-evidence.js';
import {
  getEffectiveInterviewDepth,
  getEffectiveInterviewSubPhase,
  getEffectivePlan,
  isKnownBuildGameInterviewStatus,
} from '@volley/crucible-ipc-types/build-game-types';
import { savedPlanMatchesBuilt } from '../components/plan-state';
import { pickOldestWait } from '../components/ActivityBanner';
import { formatWaitLabel } from '../wait-label';
import { planRevisionLockedForHandoff } from './plan-build-handoff';

export type WorkspaceRunKind =
  | 'ready'
  | 'starting-interview'
  | 'interview-in-progress'
  | 'drafting-plan'
  | 'waiting-for-plan-review'
  | 'build-kickoff-pending'
  | 'building'
  | 'waiting-on-user'
  | 'done'
  | 'needs-attention';

export type WorkspaceRunTone = 'neutral' | 'active' | 'waiting' | 'success' | 'warning' | 'error';

export type WorkspaceRunPrimaryCta =
  | 'start-interview'
  | 'review-plan'
  | 'start-building'
  | 'back-to-plan'
  | 'retry';

export interface WorkspaceRunState {
  kind: WorkspaceRunKind;
  label: string;
  detail?: string;
  tone: WorkspaceRunTone;
  primaryCta?: WorkspaceRunPrimaryCta;
  showBackToPlan: boolean;
  /** Plan markdown edits locked after approval / during build (handoff spec). */
  planRevisionLocked: boolean;
  disableComposerReason?: string;
  elapsedSince?: number | null;
  debugSource: string;
}

export type InitialKickoffStatus =
  | 'idle'
  | 'waiting-for-renderer-ready'
  | 'dispatching'
  | 'dispatched'
  | 'timed-out'
  | 'failed';

export type InitialKickoffFailureReason =
  | 'renderer-not-ready-timeout'
  | 'agent-send-rejected'
  | 'interview-state-missing'
  | 'anthropic-token-missing'
  | 'cloud-proxy-missing'
  | 'turn-in-flight';

export interface InitialKickoffState {
  status: InitialKickoffStatus;
  reason?: InitialKickoffFailureReason;
  detail?: string;
  startedAt?: number | null;
}

export interface BuildKickoffWatchdogState {
  status: 'idle' | 'pending' | 'stalled' | 'resume-attempted';
  committedAt?: number;
  stalledAt?: number;
}

export interface WorkspaceRunSignals {
  activitySnapshot: ActivitySnapshot | null;
  interviewState: BuildGameInterviewState | null | undefined;
  interviewStatus: BuildGameInterviewStatus | null | undefined;
  phaseProgress: PhaseProgressUpdate | null | undefined;
  foundrySnapshot?: FoundryGameStateSnapshot | null;
  isAiTyping: boolean;
  chatPersistError?: Error | { message?: string } | null;
  planModeError?: string | null;
  initialKickoff?: InitialKickoffState | null;
  isInterviewWaitingForUser?: boolean;
  buildKickoffWatchdog?: BuildKickoffWatchdogState;
  fastAnswersSubmittedAt?: number | null;
  fastPlanMissingAt?: number | null;
  autoBuildState?: AutoBuildState | null;
  nowMs?: number;
}

interface InterviewSnapshot {
  depth: KnownInterviewDepth | undefined;
  subPhase: KnownInterviewSubPhase | undefined;
  plan: string;
  status: BuildGameInterviewStatus | null;
  autoBuild: AutoBuildState | null;
  planRevision?: number;
  planHash?: number;
  lastBuiltPlanRevision?: number;
  lastBuiltPlanHash?: number;
  pendingPhaseBDecision: {
    category?: string;
    askedAt?: string;
    optionIds?: string[];
    optionLabels?: Record<string, string>;
  } | null;
}

const TERMINAL_STATUSES = new Set<BuildGameInterviewStatus>(['built', 'built-partial']);

const ATTENTION_STATUSES = new Set<BuildGameInterviewStatus>(['abandoned']);

const STALE_FINAL_REVIEW_DETAIL =
  'The build stopped before finalizing. Review the game, then resume or finalize the build.';
const STALE_BUILD_NO_LIVE_AGENT_MS = 15 * 60 * 1000;
const STALE_BUILD_DETAIL =
  'No build agents have checked in for this phase for a while. Resume the build or go back to the plan.';
const UNVERIFIED_BUILD_COMPLETION_DETAIL =
  'Rebuild or retry so Foundry can verify the current build before play or deploy.';

export function isCompletionVerificationRetryRunState(
  runState: Pick<WorkspaceRunState, 'kind' | 'primaryCta' | 'debugSource'> | null | undefined,
): boolean {
  if (runState?.kind !== 'needs-attention' || runState.primaryCta !== 'retry') return false;
  return (
    runState.debugSource.includes('completion-unverified') ||
    runState.debugSource.endsWith(':unverified')
  );
}

function interviewSnapshot(
  state: BuildGameInterviewState | null | undefined,
  statusSummary: BuildGameInterviewStatus | null | undefined,
): InterviewSnapshot {
  const depth = state ? getEffectiveInterviewDepth(state) : undefined;
  const subPhase = state ? getEffectiveInterviewSubPhase(state) : undefined;
  const plan = state ? getEffectivePlan(state) : '';
  const status =
    state?.status && isKnownBuildGameInterviewStatus(state.status)
      ? state.status
      : (statusSummary ?? null);
  const autoBuild = state?.autoBuild ?? null;
  const planRevision = state?.planRevision;
  const planHash = state?.planHash;
  const lastBuiltPlanRevision = state?.lastBuiltPlanRevision;
  const lastBuiltPlanHash = state?.lastBuiltPlanHash;
  const pendingPhaseBDecision =
    state?.phaseBDecisions?.find(
      (decision) =>
        decision.actionStatus === 'pending' &&
        (decision.answerOptionId === null || decision.answerOptionId === undefined),
    ) ?? null;
  return {
    depth,
    subPhase,
    plan,
    status,
    autoBuild,
    planRevision,
    planHash,
    lastBuiltPlanRevision,
    lastBuiltPlanHash,
    pendingPhaseBDecision,
  };
}

function hasRunningActivity(snapshot: ActivitySnapshot | null): boolean {
  if (snapshot === null) return false;
  return Object.values(snapshot.byAgentId).some((row) => row.status === 'running');
}

function hasFreshRunningActivity(snapshot: ActivitySnapshot | null): boolean {
  if (snapshot === null) return false;
  return Object.values(snapshot.byAgentId).some((row) => row.status === 'running' && !row.stale);
}

function hasFreshBuildAgentActivity(snapshot: ActivitySnapshot | null): boolean {
  if (snapshot === null || snapshot.heartbeatState === 'waiting') return false;
  return Object.values(snapshot.byAgentId).some(
    (row) => row.status === 'running' && !row.stale && row.agentId !== '__main__',
  );
}

function runningStartedAt(snapshot: ActivitySnapshot | null): number | null {
  if (snapshot === null) return null;
  const rows = Object.values(snapshot.byAgentId).filter((row) => row.status === 'running');
  if (rows.length === 0) return null;
  return Math.min(...rows.map((row) => row.startedAt));
}

function phaseProgressBeyondPlanReview(progress: PhaseProgressUpdate | null | undefined): boolean {
  return progress?.kind === 'derived' && progress.derived.current > 2;
}

function phaseProgressAtFinalReview(progress: PhaseProgressUpdate | null | undefined): boolean {
  return progress?.kind === 'derived' && progress.derived.current === 6;
}

function snapshotIsImplicitPlanReview(snapshot: FoundryGameStateSnapshot | null | undefined) {
  return (
    snapshot?.build.status === 'running' &&
    snapshot.build.terminal === false &&
    snapshot.pendingAction.kind !== 'plan-review' &&
    snapshot.phase.current === 2
  );
}

function phaseProgressBlocked(progress: PhaseProgressUpdate | null | undefined): boolean {
  return progress?.kind === 'derived' && progress.derived.blockedBy !== undefined;
}

function isTerminalInterviewStatus(status: BuildGameInterviewStatus | null | undefined): boolean {
  return status !== null && status !== undefined && TERMINAL_STATUSES.has(status);
}

function hasSubmittedValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return typeof value === 'number' || typeof value === 'boolean';
}

function answerableQuestionValues(
  value: unknown,
): Array<{ answer?: unknown; skippedAt?: unknown }> {
  const candidates = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? Object.values(value)
      : [];
  return candidates.filter(
    (candidate): candidate is { answer?: unknown; skippedAt?: unknown } =>
      candidate !== null && typeof candidate === 'object',
  );
}

function fastAnswersSubmitted(state: BuildGameInterviewState | null | undefined): boolean {
  if (!state) return false;
  const quickAnswers = state.quickAnswers;
  if (quickAnswers && typeof quickAnswers === 'object') {
    const values = Object.values(quickAnswers);
    if (values.length >= 5 && values.every(hasSubmittedValue)) return true;
  }

  const blocks = state.agents ? Object.values(state.agents) : [];
  let total = 0;
  for (const block of blocks) {
    const questions = answerableQuestionValues(block.questions);
    for (const question of questions) {
      total += 1;
      if (!hasSubmittedValue(question.answer) && !hasSubmittedValue(question.skippedAt)) {
        return false;
      }
    }
  }
  return total >= 5;
}

export function shouldSurfacePendingPhaseBDecision(args: {
  activitySnapshot: ActivitySnapshot | null;
  phaseProgress: PhaseProgressUpdate | null | undefined;
}): boolean {
  if (phaseProgressBeyondPlanReview(args.phaseProgress)) return false;
  return !(
    hasFreshRunningActivity(args.activitySnapshot) &&
    args.activitySnapshot?.heartbeatState !== 'waiting'
  );
}

/**
 * Resolve whether the Phase B decision card should render in chat.
 *
 * When the kernel snapshot reports `pendingAction.kind === 'phase-b-decision'`,
 * trust that durable signal and skip legacy activity / phase-progress gating so
 * the card stays aligned with `deriveWorkspaceRunStateFromFoundrySnapshot`.
 *
 * Args:
 *   args: Kernel snapshot, extracted interview decision, and legacy signals.
 *
 * Returns:
 *   The pending decision to surface, or null when the card should stay hidden.
 */
export function resolvePendingPhaseBDecision(args: {
  foundrySnapshot: FoundryGameStateSnapshot | null | undefined;
  decision: PhaseBDecision | null;
  activitySnapshot: ActivitySnapshot | null;
  phaseProgress: PhaseProgressUpdate | null | undefined;
}): PhaseBDecision | null {
  if (args.foundrySnapshot?.build.terminal) return null;
  if (args.foundrySnapshot && args.foundrySnapshot.pendingAction.kind !== 'phase-b-decision') {
    return null;
  }
  if (!args.decision) return null;
  if (args.foundrySnapshot?.pendingAction.kind === 'phase-b-decision') {
    return args.decision;
  }
  return shouldSurfacePendingPhaseBDecision({
    activitySnapshot: args.activitySnapshot,
    phaseProgress: args.phaseProgress,
  })
    ? args.decision
    : null;
}

function phaseProgressStartedAt(progress: PhaseProgressUpdate | null | undefined): number | null {
  if (progress?.kind !== 'derived') return null;
  const current = progress.derived.current;
  const transition = [...progress.derived.transitions]
    .reverse()
    .find((entry) => entry.phase === current);
  return parseIsoTimeMs(transition?.at);
}

type WorkspaceRunStateCore = Omit<WorkspaceRunState, 'planRevisionLocked'>;

function terminalDoneRunState(
  interviewStatus: BuildGameInterviewStatus | null,
  debugSource: string,
): WorkspaceRunStateCore {
  const isPartial = interviewStatus === 'built-partial';
  return {
    kind: 'done',
    label: isPartial ? 'Built with warnings' : 'Done',
    detail: isPartial
      ? 'The agent stopped after a partial build.'
      : 'The build-game flow is complete.',
    tone: isPartial ? 'warning' : 'success',
    showBackToPlan: false,
    debugSource,
  };
}

function unverifiedBuildCompletionState(args: {
  showBackToPlan: boolean;
  debugSource: string;
  elapsedSince?: number | null;
}): WorkspaceRunStateCore {
  return {
    kind: 'needs-attention',
    label: 'Build completion could not be verified',
    detail: UNVERIFIED_BUILD_COMPLETION_DETAIL,
    tone: 'warning',
    primaryCta: 'retry',
    showBackToPlan: args.showBackToPlan,
    elapsedSince: args.elapsedSince,
    debugSource: args.debugSource,
  };
}

function staleFinalReviewAttentionState(args: {
  showBackToPlan: boolean;
  elapsedSince: number | null;
  debugSource: 'phase-progress:stale-final-review' | 'kernel:stale-final-review';
}): WorkspaceRunStateCore {
  return {
    kind: 'needs-attention',
    label: 'Build needs attention',
    detail: STALE_FINAL_REVIEW_DETAIL,
    tone: 'warning',
    primaryCta: 'retry',
    showBackToPlan: args.showBackToPlan,
    elapsedSince: args.elapsedSince,
    debugSource: args.debugSource,
  };
}

function staleFinalReviewAttentionFromLegacy(args: {
  signals: WorkspaceRunSignals;
  interview: InterviewSnapshot;
  freshRunningActivity: boolean;
  showBackToPlan: boolean;
}): WorkspaceRunStateCore | null {
  if (args.freshRunningActivity || args.signals.isAiTyping) return null;
  if (isTerminalInterviewStatus(args.interview.status)) return null;
  if (!phaseProgressAtFinalReview(args.signals.phaseProgress)) return null;
  if (phaseProgressBlocked(args.signals.phaseProgress)) return null;

  return staleFinalReviewAttentionState({
    showBackToPlan: args.showBackToPlan,
    elapsedSince: phaseProgressStartedAt(args.signals.phaseProgress),
    debugSource: 'phase-progress:stale-final-review',
  });
}

function phaseProgressInImplementation(progress: PhaseProgressUpdate | null | undefined): boolean {
  return (
    progress?.kind === 'derived' && progress.derived.current > 2 && progress.derived.current < 6
  );
}

function staleBuildAttentionFromPhaseProgress(args: {
  signals: WorkspaceRunSignals;
  freshRunningActivity: boolean;
  showBackToPlan: boolean;
  nowMs: number | undefined;
}): WorkspaceRunStateCore | null {
  if (args.nowMs === undefined) return null;
  if (args.freshRunningActivity || args.signals.isAiTyping) return null;
  if (hasRunningActivity(args.signals.activitySnapshot)) return null;
  if (!phaseProgressInImplementation(args.signals.phaseProgress)) return null;
  if (phaseProgressBlocked(args.signals.phaseProgress)) return null;

  const elapsedSince = phaseProgressStartedAt(args.signals.phaseProgress);
  if (elapsedSince === null) return null;
  if (args.nowMs - elapsedSince < STALE_BUILD_NO_LIVE_AGENT_MS) return null;

  return {
    kind: 'needs-attention',
    label: 'Build needs attention',
    detail: STALE_BUILD_DETAIL,
    tone: 'warning',
    primaryCta: 'retry',
    showBackToPlan: args.showBackToPlan,
    elapsedSince,
    debugSource: 'phase-progress:stale-build',
  };
}

function kernelHeartbeatHasFreshWork(
  heartbeat: FoundryGameStateSnapshot['agentHeartbeat'],
): boolean {
  return heartbeat.status === 'running' && heartbeat.activeAgentIds.length > 0;
}

function kernelHeartbeatHasNoFreshWork(
  heartbeat: FoundryGameStateSnapshot['agentHeartbeat'],
): boolean {
  return (
    heartbeat.activeAgentIds.length === 0 &&
    (heartbeat.status === 'idle' || heartbeat.status === 'stale' || heartbeat.status === 'closed')
  );
}

function staleFinalReviewAttentionFromKernel(args: {
  snapshot: FoundryGameStateSnapshot;
  interviewStatus: BuildGameInterviewStatus | null;
  isAiTyping: boolean;
  elapsedSince: number | null;
}): WorkspaceRunStateCore | null {
  if (args.isAiTyping) return null;
  if (isTerminalInterviewStatus(args.interviewStatus)) return null;
  if (args.snapshot.build.terminal || args.snapshot.build.status !== 'running') return null;
  if (args.snapshot.phase.current !== 6) return null;
  if (args.snapshot.phase.blockedBy) return null;
  if (!kernelHeartbeatHasNoFreshWork(args.snapshot.agentHeartbeat)) return null;

  return staleFinalReviewAttentionState({
    showBackToPlan: true,
    elapsedSince: args.elapsedSince,
    debugSource: 'kernel:stale-final-review',
  });
}

function kickoffAttention(
  kickoff: InitialKickoffState,
  buildInFlight: boolean,
): WorkspaceRunStateCore | null {
  if (kickoff.status !== 'timed-out' && kickoff.status !== 'failed') {
    return null;
  }
  const reason = kickoff.reason ?? 'agent-send-rejected';
  // crucible#2345 — the renderer-ready handshake can time out on a perfectly
  // HEALTHY cloud-VM build: the auto-build orchestrator drives the turn
  // server-side and the agent answers via BulkInterview, so the per-slot
  // `<InterviewSlotOverlay>` never fires `interview.ready@1` and the 15s kickoff
  // watchdog times out while chunks are streaming. Do NOT scare a non-technical
  // user with "Interview did not start" when the build is actually in-flight —
  // let the computation fall through to the live build / auto-build state. Only
  // a GENUINE renderer stall (no server-side activity) warrants attention. This
  // is SCOPED to `renderer-not-ready-timeout`: every other failure reason
  // (token/proxy/state/dispatch) is a real fault and still surfaces regardless.
  if (reason === 'renderer-not-ready-timeout' && buildInFlight) {
    return null;
  }
  const detailByReason: Record<InitialKickoffFailureReason, string> = {
    'renderer-not-ready-timeout': 'The interview UI did not become ready in time.',
    'agent-send-rejected': 'The first agent message could not be sent.',
    'interview-state-missing': 'The interview state file is missing.',
    'anthropic-token-missing': 'Add an Anthropic key in Settings to start the interview.',
    'cloud-proxy-missing': 'Cloud-mode proxy configuration is missing.',
    'turn-in-flight': 'A previous turn is still winding down.',
  };
  return {
    kind: 'needs-attention',
    label: 'Interview did not start',
    detail: kickoff.detail ?? detailByReason[reason],
    tone: 'error',
    primaryCta: 'retry',
    showBackToPlan: false,
    elapsedSince: kickoff.startedAt ?? null,
    debugSource: `kickoff:${kickoff.status}:${reason}`,
  };
}

function kickoffActive(kickoff: InitialKickoffState): WorkspaceRunStateCore | null {
  if (kickoff.status !== 'waiting-for-renderer-ready' && kickoff.status !== 'dispatching') {
    return null;
  }
  const detailByStatus: Record<InitialKickoffStatus, string> = {
    idle: '',
    'waiting-for-renderer-ready': 'Preparing interview controls.',
    dispatching: 'Sending the first build-game turn.',
    dispatched: '',
    'timed-out': '',
    failed: '',
  };
  return {
    kind: 'starting-interview',
    label: 'Starting interview',
    detail: detailByStatus[kickoff.status],
    tone: 'active',
    showBackToPlan: false,
    disableComposerReason: 'Interview startup is in progress.',
    elapsedSince: kickoff.startedAt ?? null,
    debugSource: `kickoff:${kickoff.status}`,
  };
}

function quickBackToPlanEligible(
  depth: KnownInterviewDepth | undefined,
  subPhase: KnownInterviewSubPhase | undefined,
): boolean {
  return depth === 'quick' && (subPhase === 'committed' || subPhase === 'build');
}

function currentPlanWasHandedToBuild(interview: InterviewSnapshot): boolean {
  return savedPlanMatchesBuilt({
    planRevision: interview.planRevision,
    planHash: interview.planHash,
    lastBuiltPlanRevision: interview.lastBuiltPlanRevision,
    lastBuiltPlanHash: interview.lastBuiltPlanHash,
  });
}

function buildHandoffPendingRunState(args: {
  signals: WorkspaceRunSignals;
  interview: InterviewSnapshot;
  freshRunningActivity: boolean;
}): WorkspaceRunStateCore | null {
  if (!currentPlanWasHandedToBuild(args.interview)) return null;
  if (isTerminalInterviewStatus(args.interview.status)) return null;
  const showBackToPlan = args.interview.depth === 'quick';

  const phaseBActivityStarted =
    args.freshRunningActivity || phaseProgressBeyondPlanReview(args.signals.phaseProgress);
  if (phaseBActivityStarted) {
    return {
      kind: 'building',
      label: 'Building',
      detail: 'Phase B has started.',
      tone: 'active',
      primaryCta: showBackToPlan ? 'back-to-plan' : undefined,
      showBackToPlan,
      elapsedSince: runningStartedAt(args.signals.activitySnapshot),
      debugSource: 'plan-handoff:build-started',
    };
  }

  const watchdog = args.signals.buildKickoffWatchdog;
  if (watchdog?.status === 'stalled' || watchdog?.status === 'resume-attempted') {
    return {
      kind: 'needs-attention',
      label: 'Start building stalled',
      detail: 'The plan was sent to build, but Phase B did not start.',
      tone: 'warning',
      primaryCta: 'retry',
      showBackToPlan,
      elapsedSince: watchdog.stalledAt ?? watchdog.committedAt ?? null,
      debugSource: `plan-handoff:watchdog-${watchdog.status}`,
    };
  }

  return {
    kind: 'build-kickoff-pending',
    label: 'Build kickoff pending',
    detail: 'Waiting for Phase B to start.',
    tone: 'active',
    primaryCta: showBackToPlan ? 'back-to-plan' : undefined,
    showBackToPlan,
    elapsedSince: watchdog?.committedAt ?? runningStartedAt(args.signals.activitySnapshot),
    debugSource:
      watchdog?.status === 'pending'
        ? 'plan-handoff:watchdog-pending'
        : 'plan-handoff:current-plan',
  };
}

function blockedBuildAttention(
  signals: WorkspaceRunSignals,
  freshRunningActivity: boolean,
  showBackToPlan: boolean,
): WorkspaceRunStateCore | null {
  const blockedPhase =
    signals.phaseProgress?.kind === 'derived' ? signals.phaseProgress.derived.blockedBy : undefined;
  if (!blockedPhase || freshRunningActivity) {
    return null;
  }
  return {
    kind: 'needs-attention',
    label: 'Build blocked',
    detail: `Blocked by ${blockedPhase.reviewer} review.`,
    tone: 'warning',
    showBackToPlan,
    debugSource: 'phase-progress:blocked',
  };
}

function phaseProgressRunState(
  signals: WorkspaceRunSignals,
  showBackToPlan: boolean,
): WorkspaceRunStateCore | null {
  if (signals.phaseProgress?.kind !== 'derived') return null;
  const current = signals.phaseProgress.derived.current;
  const elapsedSince =
    runningStartedAt(signals.activitySnapshot) ?? phaseProgressStartedAt(signals.phaseProgress);

  if (current === 1) {
    return {
      kind: 'drafting-plan',
      label: 'Drafting plan',
      detail: 'The agent is turning your answers into a build plan.',
      tone: 'active',
      showBackToPlan: false,
      elapsedSince,
      debugSource: 'phase-progress:1',
    };
  }

  if (current === 2) {
    return {
      kind: 'waiting-for-plan-review',
      label: 'Review Build Plan',
      detail: 'Review the plan, refine it, or start building.',
      tone: 'waiting',
      primaryCta: 'start-building',
      showBackToPlan: false,
      elapsedSince,
      debugSource: 'phase-progress:2',
    };
  }

  if (current >= 7) {
    const transitionAt = [...signals.phaseProgress.derived.transitions]
      .reverse()
      .find((transition) => transition.phase === current)?.at;
    if (
      !hasVerifiedBuildCompletionEvidence({
        progress: {
          currentPhase: current,
          lastTransitionAt: transitionAt,
        },
        // Mirror the state kernel cutoff so stale phase progress cannot verify a newer plan.
        freshnessCutoffAt: legacyPhaseProgressFreshnessCutoffAt(signals.interviewState),
      })
    ) {
      return unverifiedBuildCompletionState({
        showBackToPlan,
        elapsedSince,
        debugSource: 'phase-progress:7:completion-unverified',
      });
    }
    return {
      kind: 'done',
      label: 'Ready for user testing',
      detail: 'The phase tracker is at Phase 7.',
      tone: 'success',
      showBackToPlan: false,
      debugSource: 'phase-progress:7',
    };
  }

  if (current > 2) {
    return {
      kind: 'building',
      label: 'Building',
      detail: signals.phaseProgress.derived.label || 'The agent is working through implementation.',
      tone: 'active',
      primaryCta: showBackToPlan ? 'back-to-plan' : undefined,
      showBackToPlan,
      elapsedSince,
      debugSource: 'phase-progress:build',
    };
  }

  return null;
}

function autoBuildRunState(autoBuild: AutoBuildState | null): WorkspaceRunStateCore | null {
  if (!autoBuild || autoBuild.status === 'done') return null;
  const elapsedSince = parseIsoTimeMs(autoBuild.updatedAt) ?? parseIsoTimeMs(autoBuild.armedAt);

  switch (autoBuild.status) {
    case 'armed':
      return {
        kind: 'drafting-plan',
        label: 'Preparing build',
        detail: 'Queued the automatic design-doc and build-plan pipeline.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:armed`,
      };
    case 'generating-docs':
      return {
        kind: 'drafting-plan',
        label: 'Preparing build',
        detail: 'Generating design docs from the prompt and interview answers.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:generating-docs`,
      };
    case 'exporting-package':
      return {
        kind: 'drafting-plan',
        label: 'Preparing build',
        detail: 'Creating the build plan and handoff package.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:exporting-package`,
      };
    case 'commit-starting':
      return {
        kind: 'build-kickoff-pending',
        label: 'Starting build',
        detail: 'Committing the design snapshot and dispatching the build.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:commit-starting`,
      };
    case 'cancelled':
      return {
        kind: 'needs-attention',
        label: 'Build setup cancelled',
        detail: 'Build setup stopped before the snapshot was committed.',
        tone: 'warning',
        primaryCta: 'retry',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:cancelled`,
      };
    case 'failed':
      return {
        kind: 'needs-attention',
        label: 'Build setup failed',
        detail: autoBuild.error ?? 'The automatic build setup failed before the snapshot commit.',
        tone: 'error',
        primaryCta: 'retry',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:failed`,
      };
  }
}

function formatChoiceList(labels: string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} or ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
}

function phaseBDecisionDetail(
  decision: NonNullable<InterviewSnapshot['pendingPhaseBDecision']>,
): string {
  const labels =
    decision.optionIds
      ?.map((id) => decision.optionLabels?.[id] ?? id)
      .filter((label) => label.trim().length > 0) ?? [];
  const choices = formatChoiceList(labels);
  if (choices) {
    return `Choose: ${choices}.`;
  }

  const category = decision.category;
  switch (category) {
    case 'reviewer-block':
      return 'Choose how to handle the reviewer block.';
    case 'reviewer-concerns-high':
      return 'Choose how to handle the reviewer concerns.';
    case 'install-failed':
      return 'Choose how to handle the failed install.';
    case 'missing-env-var':
      return 'Choose how to handle the missing environment variable.';
    case 'destructive-confirm':
      return 'Confirm how to proceed.';
    default:
      return 'Choose a path forward for the build.';
  }
}

function parseIsoTimeMs(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Return the state-kernel freshness marker for identity-less legacy phase progress.
 *
 * Args:
 *   state: Persisted build-game interview state from the legacy progress path.
 *
 * Returns:
 *   The newest relevant ISO timestamp, or undefined when none are parseable.
 *
 * Raises:
 *   None.
 */
function legacyPhaseProgressFreshnessCutoffAt(
  state: BuildGameInterviewState | null | undefined,
): string | undefined {
  let latest: { value: string; time: number } | null = null;
  for (const value of [state?.startedAt, state?.dispatchedAt ?? undefined, state?.planUpdatedAt]) {
    if (!value) continue;
    const time = parseIsoTimeMs(value);
    if (time === null) continue;
    if (!latest || time > latest.time) latest = { value, time };
  }
  return latest?.value;
}

function withPlanRevisionLocked(
  state: Omit<WorkspaceRunState, 'planRevisionLocked'>,
  interviewStatus: BuildGameInterviewStatus | null,
): WorkspaceRunState {
  return {
    ...state,
    planRevisionLocked: planRevisionLockedForHandoff({
      runStateKind: state.kind,
      showBackToPlan: state.showBackToPlan,
      interviewStatus,
    }),
  };
}

export function deriveWorkspaceRunState(signals: WorkspaceRunSignals): WorkspaceRunState {
  const interview = interviewSnapshot(signals.interviewState, signals.interviewStatus);
  const operationalError = signals.planModeError ?? signals.chatPersistError?.message ?? null;
  if (!operationalError) {
    const snapshotState = deriveWorkspaceRunStateFromFoundrySnapshot(signals.foundrySnapshot, {
      interviewStatus: interview.status,
      isAiTyping: signals.isAiTyping,
    });
    const freshRunningActivity =
      hasFreshRunningActivity(signals.activitySnapshot) ||
      (signals.foundrySnapshot
        ? kernelHeartbeatHasFreshWork(signals.foundrySnapshot.agentHeartbeat)
        : false);
    if (snapshotState?.kind === 'building') {
      const staleBuild = staleBuildAttentionFromPhaseProgress({
        signals,
        freshRunningActivity,
        showBackToPlan: snapshotState.showBackToPlan,
        nowMs: signals.nowMs,
      });
      if (staleBuild) {
        return withPlanRevisionLocked(staleBuild, interview.status);
      }
    }
    if (
      snapshotState &&
      !(
        snapshotState.kind === 'waiting-for-plan-review' &&
        ((snapshotIsImplicitPlanReview(signals.foundrySnapshot) &&
          hasFreshBuildAgentActivity(signals.activitySnapshot)) ||
          currentPlanWasHandedToBuild(interview))
      )
    ) {
      return snapshotState;
    }
  }
  return withPlanRevisionLocked(deriveWorkspaceRunStateCore(signals, interview), interview.status);
}

export function deriveWorkspaceRunStateFromFoundrySnapshot(
  snapshot: FoundryGameStateSnapshot | null | undefined,
  options: {
    interviewStatus?: BuildGameInterviewStatus | null;
    isAiTyping?: boolean;
  } = {},
): WorkspaceRunState | null {
  const interviewStatus = options.interviewStatus ?? null;
  const core = deriveWorkspaceRunStateCoreFromFoundrySnapshot(
    snapshot,
    interviewStatus,
    options.isAiTyping ?? false,
  );
  if (!core) return null;
  return withPlanRevisionLocked(core, interviewStatus);
}

function deriveWorkspaceRunStateCoreFromFoundrySnapshot(
  snapshot: FoundryGameStateSnapshot | null | undefined,
  interviewStatus: BuildGameInterviewStatus | null = null,
  isAiTyping = false,
): WorkspaceRunStateCore | null {
  if (!snapshot) return null;

  if (
    snapshot.layout.health.status === 'maintenance-required' ||
    snapshot.build.status === 'maintenance-required'
  ) {
    const detail =
      snapshot.build.reason ??
      snapshot.layout.health.reason ??
      snapshot.layout.repair.reason ??
      snapshot.layout.repair.actions[0] ??
      'Foundry cannot safely interpret this game state.';
    return {
      kind: 'needs-attention',
      label: 'State maintenance required',
      detail,
      tone: 'warning',
      showBackToPlan: false,
      disableComposerReason: 'Run state maintenance before continuing.',
      debugSource: 'kernel:maintenance-required',
    };
  }

  if (snapshot.pendingAction.kind === 'phase-b-decision') {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: 'Choose a path forward for the build.',
      tone: 'waiting',
      primaryCta: 'back-to-plan',
      showBackToPlan: true,
      debugSource: `kernel:pending-action:${snapshot.pendingAction.id ?? 'phase-b-decision'}`,
    };
  }

  if (snapshot.pendingAction.kind === 'plan-review') {
    return {
      kind: 'waiting-for-plan-review',
      label: 'Review Build Plan',
      detail: snapshot.pendingAction.reason ?? 'Review the plan, refine it, or start building.',
      tone: 'waiting',
      primaryCta: 'start-building',
      showBackToPlan: false,
      debugSource: `kernel:pending-action:${snapshot.pendingAction.id ?? 'plan-review'}`,
    };
  }

  if (isGeneralUserWaitKind(snapshot.pendingAction.kind)) {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: snapshot.pendingAction.reason ?? 'Foundry is waiting for input.',
      tone: 'waiting',
      showBackToPlan: false,
      debugSource: `kernel:pending-action:${snapshot.pendingAction.id ?? snapshot.pendingAction.kind}`,
    };
  }

  if (snapshot.build.terminal) {
    if (snapshot.build.status === 'failed') {
      return {
        kind: 'needs-attention',
        label: 'Build failed',
        detail: snapshot.build.reason ?? 'The build-game flow failed.',
        tone: 'error',
        primaryCta: 'retry',
        showBackToPlan: false,
        debugSource: `kernel:terminal:${snapshot.build.source ?? 'unknown'}:failed`,
      };
    }
    if (
      snapshot.build.status === 'completed' &&
      !hasVerifiedBuildCompletionEvidence({
        currentBuildRunId: snapshot.build.buildRunId,
        terminal: {
          terminalStatus: 'completed',
          buildRunId: snapshot.build.buildRunId,
        },
      })
    ) {
      return unverifiedBuildCompletionState({
        showBackToPlan: false,
        debugSource: `kernel:terminal:${snapshot.build.source ?? 'unknown'}:unverified`,
      });
    }
    const terminalSuffix = interviewStatus === 'built-partial' ? 'built-partial' : 'completed';
    return terminalDoneRunState(
      interviewStatus,
      `kernel:terminal:${snapshot.build.source ?? 'unknown'}:${terminalSuffix}`,
    );
  }

  if (snapshot.build.status !== 'running') {
    return null;
  }

  const elapsedSince = parseIsoTimeMs(snapshot.agentHeartbeat.lastHeartbeatAt);
  const current = snapshot.phase.current;
  if (
    current !== undefined &&
    current >= 7 &&
    (snapshot.build.reason === 'phase-7-unverified' || isTerminalInterviewStatus(interviewStatus))
  ) {
    // Phase 7 with an unverified projection is retryable completion, not active work.
    return unverifiedBuildCompletionState({
      showBackToPlan: true,
      elapsedSince,
      debugSource: `kernel:phase:${current}:completion-unverified`,
    });
  }
  const staleFinalReview = staleFinalReviewAttentionFromKernel({
    snapshot,
    interviewStatus,
    isAiTyping,
    elapsedSince,
  });
  if (staleFinalReview) return staleFinalReview;

  if (current === 1) {
    return {
      kind: 'drafting-plan',
      label: 'Drafting plan',
      detail: 'The agent is turning your answers into a build plan.',
      tone: 'active',
      showBackToPlan: false,
      elapsedSince,
      debugSource: 'kernel:phase:1',
    };
  }

  if (current === 2) {
    return {
      kind: 'waiting-for-plan-review',
      label: 'Review Build Plan',
      detail: 'Review the plan, refine it, or start building.',
      tone: 'waiting',
      primaryCta: 'start-building',
      showBackToPlan: false,
      elapsedSince,
      debugSource: 'kernel:phase:2',
    };
  }

  if (current && current > 2) {
    return {
      kind: 'building',
      label: 'Building',
      detail: snapshot.phase.label ?? 'The agent is working through implementation.',
      tone: 'active',
      primaryCta: 'back-to-plan',
      showBackToPlan: true,
      elapsedSince,
      debugSource: `kernel:phase:${current}`,
    };
  }

  return {
    kind: 'building',
    label: 'Building',
    detail: 'The agent is working through implementation.',
    tone: 'active',
    showBackToPlan: false,
    elapsedSince,
    debugSource: 'kernel:build:running',
  };
}

function isGeneralUserWaitKind(kind: FoundryGameStateSnapshot['pendingAction']['kind']): boolean {
  return (
    kind === 'ask-user-question' ||
    kind === 'bulk-interview' ||
    kind === 'dependency-approval' ||
    kind === 'live-preview' ||
    kind === 'scaffold-first' ||
    kind === 'change-request'
  );
}

function deriveWorkspaceRunStateCore(
  signals: WorkspaceRunSignals,
  interview: InterviewSnapshot,
): WorkspaceRunStateCore {
  const showBackToPlan = quickBackToPlanEligible(interview.depth, interview.subPhase);
  const autoBuild = signals.autoBuildState ?? interview.autoBuild;

  const operationalError = signals.planModeError ?? signals.chatPersistError?.message ?? null;
  if (operationalError) {
    return {
      kind: 'needs-attention',
      label: 'Needs attention',
      detail: operationalError,
      tone: 'error',
      primaryCta: 'retry',
      showBackToPlan,
      debugSource: 'error:workspace',
    };
  }

  // crucible#2345 — computed BEFORE the kickoff block so the kickoff watchdog can
  // tell whether the build is actually in-flight (an active auto-build is one of
  // the live-build signals). Returned below, after the kickoff check, so the
  // existing precedence (kickoff attention → auto-build → activity) is preserved.
  const autoBuildState = autoBuildRunState(autoBuild);

  const kickoff = signals.initialKickoff ?? null;
  const kickoffInProgress = kickoff !== null && kickoff.status !== 'idle';
  if (kickoffInProgress) {
    // Is the build genuinely streaming/progressing server-side? If so, a
    // renderer-ready handshake timeout is benign (cloud-VM orchestrator drives
    // the turn; the agent answers via BulkInterview), and kickoffAttention
    // suppresses the scary "Interview did not start" for that reason only.
    const buildInFlight =
      signals.isAiTyping === true ||
      isTerminalInterviewStatus(signals.interviewStatus) ||
      autoBuildState !== null ||
      hasFreshRunningActivity(signals.activitySnapshot);
    const attention = kickoffAttention(kickoff, buildInFlight);
    if (attention) return attention;
  }

  if (autoBuildState) return autoBuildState;

  const wait = signals.activitySnapshot
    ? pickOldestWait(signals.activitySnapshot.outstandingWaits)
    : null;
  const heartbeatWaiting = signals.activitySnapshot?.heartbeatState === 'waiting';
  const freshRunningActivity = hasFreshRunningActivity(signals.activitySnapshot);
  if (wait && (heartbeatWaiting || !freshRunningActivity)) {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: formatWaitLabel(wait),
      tone: 'waiting',
      primaryCta:
        interview.depth === 'quick' &&
        (interview.subPhase === 'plan' ||
          interview.subPhase === 'refine' ||
          interview.subPhase === 'back-to-plan')
          ? 'start-building'
          : undefined,
      showBackToPlan,
      elapsedSince: wait.openedAt,
      debugSource: `activity:wait:${wait.kind}`,
    };
  }

  if (
    signals.isInterviewWaitingForUser ||
    (interview.status === 'in-progress' &&
      interview.depth !== 'quick' &&
      !freshRunningActivity &&
      !signals.isAiTyping)
  ) {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: 'Answer the interview questions.',
      tone: 'waiting',
      showBackToPlan,
      elapsedSince: runningStartedAt(signals.activitySnapshot),
      debugSource: signals.isInterviewWaitingForUser
        ? 'interview-ui:waiting-for-user'
        : 'interview-status:in-progress:no-running-activity',
    };
  }

  if (kickoffInProgress) {
    const active = kickoffActive(kickoff);
    if (active) return active;
  }

  if (ATTENTION_STATUSES.has(interview.status as BuildGameInterviewStatus)) {
    return {
      kind: 'needs-attention',
      label: 'Build needs attention',
      detail: 'The build-game flow was abandoned.',
      tone: 'warning',
      primaryCta: 'retry',
      showBackToPlan,
      debugSource: `interview-status:${interview.status}`,
    };
  }

  if (interview.status && TERMINAL_STATUSES.has(interview.status)) {
    return unverifiedBuildCompletionState({
      showBackToPlan,
      debugSource: `interview-status:${interview.status}:completion-unverified`,
    });
  }

  if (
    interview.pendingPhaseBDecision &&
    shouldSurfacePendingPhaseBDecision({
      activitySnapshot: signals.activitySnapshot,
      phaseProgress: signals.phaseProgress,
    })
  ) {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: phaseBDecisionDetail(interview.pendingPhaseBDecision),
      tone: 'waiting',
      primaryCta: 'back-to-plan',
      showBackToPlan,
      elapsedSince: parseIsoTimeMs(interview.pendingPhaseBDecision.askedAt),
      debugSource: `interview-state:phase-b-decision:${interview.pendingPhaseBDecision.category ?? 'unknown'}`,
    };
  }

  const staleFinalReview = staleFinalReviewAttentionFromLegacy({
    signals,
    interview,
    freshRunningActivity,
    showBackToPlan,
  });
  if (staleFinalReview) return staleFinalReview;

  const staleBuild = staleBuildAttentionFromPhaseProgress({
    signals,
    freshRunningActivity,
    showBackToPlan,
    nowMs: signals.nowMs,
  });
  if (staleBuild) return staleBuild;

  const handoffPending = buildHandoffPendingRunState({
    signals,
    interview,
    freshRunningActivity,
  });
  if (handoffPending) return handoffPending;

  if (interview.depth === 'quick') {
    const hasPlan = interview.plan.trim().length > 0;
    if (interview.subPhase === 'committed') {
      const watchdog = signals.buildKickoffWatchdog;
      const phaseBActivityStarted =
        freshRunningActivity || phaseProgressBeyondPlanReview(signals.phaseProgress);
      if (
        watchdog &&
        !phaseBActivityStarted &&
        (watchdog.status === 'stalled' || watchdog.status === 'resume-attempted')
      ) {
        return {
          kind: 'needs-attention',
          label: 'Start building stalled',
          detail: 'The plan was approved, but Phase B did not start.',
          tone: 'warning',
          primaryCta: 'retry',
          showBackToPlan: true,
          elapsedSince: watchdog.stalledAt ?? watchdog.committedAt ?? null,
          debugSource: `interview-state:quick:committed:watchdog-${watchdog.status}`,
        };
      }
      if (phaseProgressBeyondPlanReview(signals.phaseProgress)) {
        return {
          kind: 'building',
          label: 'Building',
          detail: 'Phase B has started.',
          tone: 'active',
          primaryCta: 'back-to-plan',
          showBackToPlan: true,
          elapsedSince: runningStartedAt(signals.activitySnapshot),
          debugSource: 'phase-progress:build-after-committed',
        };
      }
      if (hasFreshBuildAgentActivity(signals.activitySnapshot)) {
        return {
          kind: 'building',
          label: 'Building',
          detail: 'Build agents are working through implementation.',
          tone: 'active',
          primaryCta: 'back-to-plan',
          showBackToPlan: true,
          elapsedSince: runningStartedAt(signals.activitySnapshot),
          debugSource: 'activity:build-agent-after-committed',
        };
      }
      return {
        kind: 'build-kickoff-pending',
        label: 'Build kickoff pending',
        detail: 'Waiting for Phase B to start.',
        tone: 'active',
        primaryCta: 'back-to-plan',
        showBackToPlan: true,
        elapsedSince: watchdog?.committedAt ?? runningStartedAt(signals.activitySnapshot),
        debugSource:
          watchdog?.status === 'pending'
            ? 'interview-state:quick:committed:watchdog-pending'
            : 'interview-state:quick:committed',
      };
    }
    if (interview.subPhase === 'build') {
      const blocked = blockedBuildAttention(signals, freshRunningActivity, true);
      if (blocked) return blocked;
      return {
        kind: 'building',
        label: 'Building',
        detail: 'The agent is working through the implementation.',
        tone: 'active',
        primaryCta: 'back-to-plan',
        showBackToPlan: true,
        elapsedSince: runningStartedAt(signals.activitySnapshot),
        debugSource: 'interview-state:quick:build',
      };
    }
    if (
      interview.subPhase === 'plan' ||
      interview.subPhase === 'refine' ||
      interview.subPhase === 'back-to-plan'
    ) {
      if (hasPlan) {
        return {
          kind: 'waiting-for-plan-review',
          label: 'Review Build Plan',
          detail: 'Review the plan, refine it, or start building.',
          tone: 'waiting',
          primaryCta: 'start-building',
          showBackToPlan: false,
          elapsedSince: runningStartedAt(signals.activitySnapshot),
          debugSource: `interview-state:quick:${interview.subPhase}:plan-present`,
        };
      }
      return {
        kind: 'drafting-plan',
        label: 'Drafting plan',
        detail: 'The agent is turning your answers into a build plan.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince: runningStartedAt(signals.activitySnapshot),
        debugSource: `interview-state:quick:${interview.subPhase}:plan-empty`,
      };
    }
    if (interview.subPhase === 'quick') {
      if (hasPlan) {
        return {
          kind: 'waiting-for-plan-review',
          label: 'Review Build Plan',
          detail: 'Review the plan, refine it, or start building.',
          tone: 'waiting',
          primaryCta: 'start-building',
          showBackToPlan: false,
          elapsedSince: runningStartedAt(signals.activitySnapshot),
          debugSource: 'interview-state:quick:answers-submitted:plan-present',
        };
      }
      const fastAnswersReady =
        hasPlan === false &&
        (fastAnswersSubmitted(signals.interviewState) || signals.fastAnswersSubmittedAt != null);
      if (signals.fastPlanMissingAt != null && fastAnswersReady) {
        return {
          kind: 'needs-attention',
          label: 'Build plan needs attention',
          detail: 'Fast answers were submitted, but no build plan was saved.',
          tone: 'warning',
          primaryCta: 'retry',
          showBackToPlan: false,
          elapsedSince: signals.fastPlanMissingAt,
          debugSource: 'interview-state:quick:answers-submitted:plan-missing',
        };
      }
      if (fastAnswersReady) {
        return {
          kind: 'drafting-plan',
          label: 'Preparing Build Plan',
          detail: 'Turning your Fast answers into a build plan.',
          tone: 'active',
          showBackToPlan: false,
          elapsedSince:
            signals.fastAnswersSubmittedAt ?? runningStartedAt(signals.activitySnapshot),
          debugSource: fastAnswersSubmitted(signals.interviewState)
            ? 'interview-state:quick:answers-submitted'
            : 'renderer:fast-answers-submitted',
        };
      }
      return {
        kind: 'interview-in-progress',
        label: 'Interview in progress',
        detail: 'Answer the quick build-game questions.',
        tone: 'active',
        showBackToPlan: false,
        debugSource: 'interview-state:quick:quick',
      };
    }
  }

  if (interview.status === 'dispatched') {
    return {
      kind: 'build-kickoff-pending',
      label: 'Build kickoff pending',
      detail: 'Interview complete; waiting for implementation to start.',
      tone: 'active',
      showBackToPlan,
      debugSource: 'interview-status:dispatched',
    };
  }

  const blocked = blockedBuildAttention(signals, freshRunningActivity, showBackToPlan);
  if (blocked) return blocked;

  if (interview.status === 'building') {
    return {
      kind: 'building',
      label: 'Building',
      detail: 'The agent is working through implementation.',
      tone: 'active',
      showBackToPlan,
      elapsedSince: runningStartedAt(signals.activitySnapshot),
      debugSource: 'interview-status:building',
    };
  }

  if (interview.status === 'in-progress' || signals.isAiTyping) {
    return {
      kind: 'interview-in-progress',
      label: 'Interview in progress',
      detail: 'The agent is asking questions or processing your answer.',
      tone: 'active',
      showBackToPlan,
      elapsedSince: runningStartedAt(signals.activitySnapshot),
      debugSource:
        interview.status === 'in-progress' ? 'interview-status:in-progress' : 'chat:is-ai-typing',
    };
  }

  if (hasRunningActivity(signals.activitySnapshot)) {
    return {
      kind: 'building',
      label: 'Agent working',
      detail: 'Live activity is in progress.',
      tone: 'active',
      showBackToPlan,
      elapsedSince: runningStartedAt(signals.activitySnapshot),
      debugSource: 'activity:running',
    };
  }

  const progressState = phaseProgressRunState(signals, showBackToPlan);
  if (progressState) return progressState;

  return {
    kind: 'ready',
    label: 'Ready',
    tone: 'neutral',
    showBackToPlan: false,
    debugSource: 'default:ready',
  };
}
